import { useEffect, useRef } from 'react';
import axiosInstance from '../services/api';

const HEARTBEAT_MS = 60 * 1000;
const ACTIVE_WINDOW_MS = 2 * 60 * 1000;
const MAX_ELAPSED_MS = 75 * 1000;

function getActivitySessionId() {
  const existingSessionId = sessionStorage.getItem('sessionId');
  if (existingSessionId) return existingSessionId;

  const existingActivityId = sessionStorage.getItem('activitySessionId');
  if (existingActivityId) return existingActivityId;

  const generatedId = window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : `activity_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  sessionStorage.setItem('activitySessionId', generatedId);
  return generatedId;
}

export default function useUserActivityTracker(currentUser, pathname) {
  const currentUserUid = currentUser?.uid || null;
  const routeRef = useRef(pathname || '/');
  const lastTickAtRef = useRef(Date.now());
  const lastActivityAtRef = useRef(Date.now());
  const pendingPageViewsRef = useRef(0);
  const pendingEventCountRef = useRef(0);
  const inFlightRef = useRef(false);
  const flushRef = useRef(null);

  const noteActivity = () => {
    lastActivityAtRef.current = Date.now();
    pendingEventCountRef.current += 1;
  };

  useEffect(() => {
    if (!currentUserUid) {
      flushRef.current = null;
      return undefined;
    }

    let cancelled = false;
    lastTickAtRef.current = Date.now();
    lastActivityAtRef.current = Date.now();

    const flush = async (reason = 'interval') => {
      if (cancelled || inFlightRef.current) return;

      const now = Date.now();
      const elapsedMs = Math.min(Math.max(now - lastTickAtRef.current, 0), MAX_ELAPSED_MS);
      const isVisible = document.visibilityState !== 'hidden';
      const isRecentlyActive = now - lastActivityAtRef.current <= ACTIVE_WINDOW_MS;
      const activeMs = isVisible && isRecentlyActive ? elapsedMs : 0;
      const pageViews = pendingPageViewsRef.current;
      const eventCount = pendingEventCountRef.current;

      lastTickAtRef.current = now;

      if (reason === 'interval' && activeMs === 0 && pageViews === 0 && eventCount === 0) {
        return;
      }

      pendingPageViewsRef.current = 0;
      pendingEventCountRef.current = 0;
      inFlightRef.current = true;

      try {
        await axiosInstance.post('/user-activity/heartbeat', {
          sessionId: getActivitySessionId(),
          route: routeRef.current,
          activeMs,
          pageViews,
          eventCount,
          visible: isVisible,
          reason
        });
      } catch (error) {
        pendingPageViewsRef.current += pageViews;
        pendingEventCountRef.current += eventCount;
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[Activity] heartbeat failed:', error?.message || error);
        }
      } finally {
        inFlightRef.current = false;
      }
    };

    flushRef.current = flush;

    const activityEvents = ['click', 'keydown', 'touchstart', 'scroll'];
    activityEvents.forEach(eventName => {
      window.addEventListener(eventName, noteActivity, { passive: true });
    });

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flush('hidden');
      } else {
        noteActivity();
      }
    };

    window.addEventListener('focus', noteActivity);
    document.addEventListener('visibilitychange', onVisibilityChange);

    const startTimer = window.setTimeout(() => flush('start'), 3000);
    const interval = window.setInterval(() => flush('interval'), HEARTBEAT_MS);

    return () => {
      cancelled = true;
      flushRef.current = null;
      window.clearTimeout(startTimer);
      window.clearInterval(interval);
      activityEvents.forEach(eventName => {
        window.removeEventListener(eventName, noteActivity);
      });
      window.removeEventListener('focus', noteActivity);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [currentUserUid]);

  useEffect(() => {
    routeRef.current = pathname || '/';

    if (!currentUserUid) return undefined;

    lastActivityAtRef.current = Date.now();
    pendingPageViewsRef.current += 1;

    const timer = window.setTimeout(() => {
      flushRef.current?.('route');
    }, 500);

    return () => window.clearTimeout(timer);
  }, [currentUserUid, pathname]);
}
